// Layer 3 (Transformation): Year 1 cumulative feed drawn against cumulative
// budget (locked comp `planning.html` A, "Year 1 feed against budget").
//
// A pure decomposition of figures the route already prints; it adds none:
// - the budget is the twelve monthly budget rows, cumulated (the zero line);
// - the actual is M1–M3 actuals, then the Year-1-to-date point on the band's
//   basis (the monthly view's to-date actual against its to-date budget);
// - each scenario path starts at that point, adds the scenario's Month-4 close
//   (rollingForecastView().forecastToClose) and then each later month's
//   scheduled hours × the scenario rate. It is the existing full-Year-1 formula
//   (to date + remaining hours × rate) taken month by month, so its end point
//   is that scenario's full-year tonnes (asserted in Planning.probe.ts);
// - stretch is the annual stretch multiplier applied to cumulative budget.

import { ANNUAL_STRETCH_MULTIPLIER, PLANNING_MONTH_PROFILE } from '@/versions/v1/shared/bunker-hill-planning-seed';
import type { MonthlyPlanView, PlanningScenarioName, RollingForecastView } from './Scene.transform';

/** `m` is months since 01 Mar 2026 (0 … 12); `cumSt` the cumulative dst;
 *  `vsBudgetSt` the same point less cumulative budget. */
export type AgainstBudgetPoint = { m: number; cumSt: number; vsBudgetSt: number };

export type AgainstBudgetScenario = {
  name: PlanningScenarioName;
  rateStph: number;
  points: AgainstBudgetPoint[];
  /** The path's end: equals the scenario's full-Year-1 tonnes. */
  endSt: number;
};

export type YearAgainstBudgetView = {
  budgetCumSt: number[];
  actual: AgainstBudgetPoint[];
  cursorM: number;
  scenarios: AgainstBudgetScenario[];
  stretch: AgainstBudgetPoint[];
  stretchLabel: string;
  domain: [number, number];
  ticks: number[];
  months: { code: string; month: string }[];
  currentMonthIndex: number;
};

const CURRENT_MONTH_INDEX = 3;

function niceStep(span: number): number {
  const raw = span / 5;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / magnitude;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10) * magnitude;
}

export function yearAgainstBudgetView(
  monthly: MonthlyPlanView,
  rolling: RollingForecastView,
): YearAgainstBudgetView {
  const budgetCumSt = [0];
  for (const row of monthly.rows) budgetCumSt.push(budgetCumSt[budgetCumSt.length - 1]! + row.budgetSt);
  // The to-date point sits on the band's basis: x is where cumulative budget
  // reaches the monthly view's to-date budget, so the drawn gap is the band's
  // variance whatever window that budget uses.
  const currentBudget = monthly.rows[CURRENT_MONTH_INDEX]!.budgetSt;
  const cursorM = CURRENT_MONTH_INDEX + (monthly.yearToDateBudgetSt - budgetCumSt[CURRENT_MONTH_INDEX]!) / currentBudget;
  const actual: AgainstBudgetPoint[] = [{ m: 0, cumSt: 0, vsBudgetSt: 0 }];
  let priorActual = 0;
  monthly.rows.slice(0, CURRENT_MONTH_INDEX).forEach((row, index) => {
    priorActual += row.actualSt ?? 0;
    actual.push({ m: index + 1, cumSt: priorActual, vsBudgetSt: priorActual - budgetCumSt[index + 1]! });
  });
  const toDate: AgainstBudgetPoint = {
    m: cursorM,
    cumSt: monthly.yearToDateActualSt,
    vsBudgetSt: monthly.yearToDateActualSt - monthly.yearToDateBudgetSt,
  };
  actual.push(toDate);

  const order: readonly PlanningScenarioName[] = ['Optimistic', 'Central', 'Conservative'];
  const scenarios = order.map((name): AgainstBudgetScenario => {
    const close = rolling.forecastToClose.scenarios.find((scenario) => scenario.name === name)!;
    const points: AgainstBudgetPoint[] = [toDate];
    let cum = priorActual + close.tonnes;
    points.push({ m: CURRENT_MONTH_INDEX + 1, cumSt: cum, vsBudgetSt: cum - budgetCumSt[CURRENT_MONTH_INDEX + 1]! });
    monthly.rows.slice(CURRENT_MONTH_INDEX + 1).forEach((row, offset) => {
      const monthEnd = CURRENT_MONTH_INDEX + 2 + offset;
      cum += row.scheduledDays * 24 * close.rateStph;
      points.push({ m: monthEnd, cumSt: cum, vsBudgetSt: cum - budgetCumSt[monthEnd]! });
    });
    return { name, rateStph: close.rateStph, points, endSt: cum };
  });

  const stretch = budgetCumSt.map((value, m) => ({ m, cumSt: value * ANNUAL_STRETCH_MULTIPLIER, vsBudgetSt: value * (ANNUAL_STRETCH_MULTIPLIER - 1) }));

  const values = [
    ...actual.map((point) => point.vsBudgetSt),
    ...scenarios.flatMap((scenario) => scenario.points.map((point) => point.vsBudgetSt)),
    ...stretch.map((point) => point.vsBudgetSt),
    0,
  ];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = niceStep(max - min);
  // Tight domain: a little air past the extremes, gridlines only inside it,
  // plus a half-step tick below zero when the behind-budget dip reaches it.
  const lo = min - (max - min) * 0.08;
  const hi = max + (max - min) * 0.05;
  const ticks: number[] = [];
  if (lo <= -step / 2) ticks.push(-step / 2);
  for (let tick = 0; tick <= hi; tick += step) ticks.push(tick);

  return {
    budgetCumSt,
    actual,
    cursorM,
    scenarios,
    stretch,
    stretchLabel: `Stretch +${((ANNUAL_STRETCH_MULTIPLIER - 1) * 100).toFixed(0)}%`,
    domain: [lo, hi],
    ticks,
    months: PLANNING_MONTH_PROFILE.map((month, index) => ({ code: `M${index + 1}`, month: month.month })),
    currentMonthIndex: CURRENT_MONTH_INDEX,
  };
}

/** Axis tick label: `0`, `+20k`, `−10k`. */
export function formatVsBudgetTick(value: number): string {
  if (value === 0) return '0';
  return `${value > 0 ? '+' : '−'}${Math.abs(value / 1000).toFixed(0)}k`;
}
